"use client";

import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { ParsedGoldItem } from "@/type/goldPageType";
import { formatDate, formatIndianNumber, capitalize } from "@/utils/commonFunctions";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";

export default function GoldRateSection({
  cities,
  goldData,
  updatedDate,
  selectedCity = "India",
  yesterdayData = true,
}: {
  cities: string[];
  goldData: ParsedGoldItem[];
  updatedDate: Date | string;
  yesterdayData?: boolean;
  selectedCity?: string;
}) {
  const [city, setCity] = useState(selectedCity);
  const [search, setSearch] = useState("");
  const [isActive, setIsActive] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }

    if (!isActive) {
      setSearch("");
    }
  }, [isActive]);

  const filteredCities = cities
    .filter((c) => c.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  return (
    <>
      {/* Heading */}
      <div className="text-2.5xl xs:text-3xl sm:text-4xl lg:text-5xl px-2.5 sm:px-0 text-center text-white font-bold mb-2.5 sm:mb-4 flex items-center justify-center flex-wrap gap-2">
        <h1>Gold Rate Today in {capitalize(selectedCity)}</h1>
        <span>- {formatDate(updatedDate)}</span>
      </div>

      {/* Dropdown */}
      <div className="mt-6 relative px-2.5 sm:px-0">
        <div className="flex flex-col xxs:flex-row items-center justify-center gap-3 xxs:gap-7">
          <span className="text-lg leading-1 sm:text-xl sm:leading-7 font-light text-white/75">
            Select Your City:
          </span>

          <div className="w-full max-w-64 relative">
            <button
              ref={dropdownRef}
              onClick={() => setIsActive(!isActive)}
              aria-label="Select to Open Dropdown to select the City"
              className="w-full flex items-center justify-between gap-5 bg-white/10 border border-[#b1960098] focus:border-[#B19600] px-4 sm:px-5 py-2 sm:py-3 rounded-full sm:text-xl sm:leading-1 text-white capitalize"
            >
              {city}
              <Icon.dropdownDownArrow
                className={`w-3.5 transition duration-300 ${isActive && "rotate-180"}`}
              />
            </button>

            <CustomDropdown
              isOpen={isActive}
              onClose={() => setIsActive(false)}
              excludeRefs={[dropdownRef]}
              className="absolute top-full left-0 w-full max-w-xs mx-auto mt-1 rounded-xl border border-[#B19600] bg-white/10 backdrop-blur-md shadow-md z-10 p-2"
            >
              <label className="group block sticky top-0 px-4 py-2 max-w-lg rounded-xl bg-transparent transition border border-[#b9b9b9] placeholder-white/40 hover:border-white outline-hidden">
                <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition text-[#b9b9b9] group-hover:text-white" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search Funds..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-transparent px-6 transition text-[#b9b9b9] group-hover:text-white"
                />
              </label>

              <div className="max-h-64 overflow-y-auto mt-1 scrollbar-xs space-y-1">
                {filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <Link
                      href={`/buy-digital-gold/gold-price/${city.toLowerCase()}`}
                      key={city}
                      onClick={() => {
                        setCity(city);
                        setIsActive(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-xl transition"
                    >
                      {city}
                    </Link>
                  ))
                ) : (
                  <div className="w-full text-center px-3 py-2 text-white">
                    No City Found
                  </div>
                )}
              </div>
            </CustomDropdown>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="w-full 2xl:max-w-6xl mx-auto mt-10 flex items-center xs:justify-center sm:grid sm:grid-cols-3 gap-5 md:gap-6 md:border border-white/45 md:rounded-9xl md:p-8 overflow-x-auto sm:overflow-x-hidden scrollbar-none">
        {goldData.map((item, idx) => (
          <div
            key={idx}
            className="shrink-0 first-of-type:ml-2.5 last-of-type:mr-2.5 sm:first-of-type:ml-0 sm:last-of-type:mr-0 bg-white/10 border border-[#2f2f2f] rounded-4xl p-4 md:p-5"
          >
            <p className="text-white/70 leading-1 font-light">
              {item.goldType}K Gold
            </p>

            <div className="text-white flex items-end gap-2 mt-3">
              <p className="text-xl sm:text-2xl sm:leading-8 md:text-3xl md:leading-9 font-medium leading-1">
                <span className="text-white/70">₹</span>{" "}
                {formatIndianNumber(item.todaysPrice)}
              </p>
              <p className="text-2sm sm:text-base sm:leading-6 md:text-lg md:leading-6.5 leading-1">
                per gram
              </p>
            </div>

            {yesterdayData && (
              <div className="flex items-center gap-4 mt-3 font-light text-sm 2md:text-base">
                <p className="text-white/70">
                  Yesterday: ₹ {formatIndianNumber(item.yesterdaysPrice)}
                </p>

                <p
                  className={`flex items-center gap-1 font-normal ${item.change > 0 ? "text-secondaryGreen" : "text-[#ff3535]"}`}
                >
                  <Icon.growthSVG
                    className={`w-5 h-5 lg:w-6 lg:h-6 ${item.change > 0 ? "text-secondaryGreen" : "text-[#ff3535] -scale-y-100"}`}
                  />{" "}
                  {item.change}%
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
